import moment from 'moment';
import queryString from 'query-string';
import {
  AUTOMATION_STATUS,
  IPolicy,
  IPolicyProvider,
  IPolicyScan,
  IQuote,
} from 'types';
import {
  FIELD_KEYS_FOR_COMPLETION_BY_NAME,
  FIELD_KEYS_AMOUNT_BY_NAME,
  FIELD_KEYS_EXCESS_BY_NAME,
} from 'constants/insurance';
import {
  MARKET_SCAN_QUESTIONS_BY_NAME,
  MARKET_SCAN_FIELDS_TO_IGNORE,
  MARKET_SCAN_POLICIES,
  LIFE_REFERRAL_ITEMS,
} from 'constants/marketScan';
import { MARKET_SCAN_PURCHASE_ROUTE } from 'constants/routes';
import { isPolicyInReview } from './policy';
import { PolicyStatus, ProviderNames } from 'constants/policy';
import { FREQUENCY } from 'constants/options';
import { convertFrequency } from './form';
import { prettifyNumber } from './number';
import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';
import { Estimate } from 'components/svgs/Estimate';

const MARKET_SCAN_REQUEST_DAYS = 30;

export const getFieldKeysForMarketScan = (policy, subTypes) => {
  const { policyType } = policy;
  const fieldKeysCommon =
    FIELD_KEYS_FOR_COMPLETION_BY_NAME[policyType] ||
    FIELD_KEYS_FOR_COMPLETION_BY_NAME.default;
  const fieldKeysByType = R.propOr(
    [],
    policyType,
    MARKET_SCAN_QUESTIONS_BY_NAME,
  )
    .filter((f) => (f.showKeyValue ? f.showKeyValue(policy) : true))
    .map(R.prop('fieldKey'));
  const amountKeys =
    FIELD_KEYS_AMOUNT_BY_NAME[policy.policyType] ||
    FIELD_KEYS_AMOUNT_BY_NAME.default;
  const excessKeys =
    FIELD_KEYS_EXCESS_BY_NAME[policy.policyType] ||
    FIELD_KEYS_EXCESS_BY_NAME.default;

  const fieldKeys = [
    ...fieldKeysCommon,
    ...fieldKeysByType,
    ...amountKeys,
    ...excessKeys,
  ].filter(
    (f) =>
      !R.includes(f, [
        ...MARKET_SCAN_FIELDS_TO_IGNORE,
        'renewalDate',
        'policyProviderId',
      ]),
  );

  if (subTypes.length > 0) {
    return R.append('policySubTypeId', fieldKeys);
  }

  return fieldKeys;
};

export const canRequestMarketScanByDate = ({ lastPolicyScanDate }: IPolicy) => {
  if (!lastPolicyScanDate) {
    return true;
  }

  const date = moment(lastPolicyScanDate);
  const diffDays = moment().diff(date, 'days');
  return diffDays > MARKET_SCAN_REQUEST_DAYS;
};

export const getPurchaseLink = (providerName: string) => {
  const { id, sk } = queryString.parse(location.search.replace('?', ''));
  return `${MARKET_SCAN_PURCHASE_ROUTE}?id=${id}&sk=${sk}&providerName=${providerName}`;
};

export const claimsHistoryCleanUp = (model) => {
  const newModel = { ...model };
  if (!newModel.accidentHistory) {
    newModel.accidentClaimCount = 0;
    delete newModel.claimType1;
    delete newModel.accidentClaimDate1;
    delete newModel.accidentClaimDate2;
    delete newModel.accidentFault;
  }
  return newModel;
};

export const filterAutomationStatus = (
  automationStatus: AutomationStatusLabel,
  policyScan: IPolicyScan,
) => {
  if (automationStatus === AutomationStatusLabel.CompletedReview) {
    return policyScan.automationHasError !== undefined;
  }
  if (automationStatus === AutomationStatusLabel.ManualProcessNeeded) {
    return (
      policyScan.automationStatus === AUTOMATION_STATUS.MANUAL_COMPLETED_NEEDED
    );
  }
  if (automationStatus === AutomationStatusLabel.PendingReview) {
    return (
      policyScan.automationStatus === AUTOMATION_STATUS.MANUAL_REVIEW_NEEDED &&
      policyScan.automationHasError === undefined
    );
  }
};

export enum AutomationStatusLabel {
  CompletedReview = 'CompletedReview',
  PendingReview = 'PendingReview',
  ManualProcessNeeded = 'ManualProcessNeeded',
}

const AnnualDistanceDrivenOptions = {
  KM_5: 'Less than 5km',
  KM_5_10: '5-10km',
  KM_10_20: '10-20km',
  KM_20: 'More than 20km',
};

export const convertAnnualDistanceDriven = (distance: number) => {
  if (distance < 5000) {
    return AnnualDistanceDrivenOptions.KM_5;
  } else if (distance > 5000 && distance < 10000) {
    return AnnualDistanceDrivenOptions.KM_5_10;
  } else if (distance > 10000 && distance < 20000) {
    return AnnualDistanceDrivenOptions.KM_10_20;
  } else {
    return AnnualDistanceDrivenOptions.KM_20;
  }
};

export const isMarketScanPolicy = (policy) => {
  return (
    !isPolicyInReview(policy) &&
    policy.status !== PolicyStatus.MARKET_SCAN_HOLDER &&
    R.includes(policy.policyType, MARKET_SCAN_POLICIES) &&
    canRequestMarketScanByDate(policy)
  );
};

export const isNonCompareMsOnly = R.includes(R.__, [
  INSURANCE_TYPES_NAMES.BICYCLE,
  INSURANCE_TYPES_NAMES.HEALTH,
  INSURANCE_TYPES_NAMES.MBI,
  INSURANCE_TYPES_NAMES.PET,
  INSURANCE_TYPES_NAMES.TRAVEL,
]);

export const isLifeReferral = (policyType: string) => {
  return !!LIFE_REFERRAL_ITEMS.find(R.propEq('key', policyType));
};

export const findCheapestProviderIdx = (
  quotes,
  predicate = R.T,
  defaultFrequency: string = FREQUENCY.YEARLY,
) => {
  let cheapestPrice = Infinity;
  let cheapestIdx = null;

  quotes.forEach((quote, idx) => {
    const yearly = quote.premiums.find(
      (premium) => premium.frequency === defaultFrequency,
    );

    let premium = yearly?.premium;

    if (!premium) {
      const firstPremium = quote.premiums[0];
      if (firstPremium) {
        premium = convertFrequency(
          firstPremium.premium,
          firstPremium.frequency,
          defaultFrequency,
        );
      }
    }

    if ((parseFloat(premium) || Infinity) < cheapestPrice && predicate(quote)) {
      cheapestPrice = parseFloat(premium);
      cheapestIdx = idx;
    }
  });

  return cheapestIdx;
};

export const getPremiumByFrequency = (premiums, frequency) => {
  const premByFrequency = premiums?.find(R.propEq('frequency', frequency));
  return premByFrequency?.premium;
};

export const getPremiumIncludeAddon = (quote: any, frequency: FREQUENCY, premium: number, windscreenToggle: boolean, roadsideToggle: boolean) => {
  if (!windscreenToggle && !roadsideToggle) {
    return `$${prettifyNumber(premium)}`
  } else {


    if (frequency === FREQUENCY.MONTHLY) {
      let totalMonthly = Number(premium);

      if (windscreenToggle) {
        totalMonthly = totalMonthly + (quote.windscreenMonthly || 0)
      }

      if (roadsideToggle) {
        totalMonthly = totalMonthly + (quote.roadsideMonthly || 0)
      }

      return `$${prettifyNumber(totalMonthly)}`
    } else if (frequency === FREQUENCY.YEARLY) {
      let totalYearly = Number(premium);

      if (windscreenToggle) {
        totalYearly = totalYearly + (quote.windscreenYearly || 0)
      }

      if (roadsideToggle) {
        totalYearly = totalYearly + (quote.roadsideYearly || 0)
      }

      return `$${prettifyNumber(totalYearly)}`
    }
  }

}

export const getCurrentPremiumAmount = (
  { premiums },
  frequency = FREQUENCY.YEARLY,
) => {
  const p = premiums[0];
  if (!p) {
    return null;
  }

  const premium = getPremiumByFrequency(premiums, frequency);
  const converted =
    premium || convertFrequency(parseFloat(p.premium), p.frequency, frequency);
  return `$${prettifyNumber(converted)}`;
};

export const getQuotePremium = (
  quote,
  frequency: FREQUENCY,
  toggledFrequency?: FREQUENCY,
  shouldRoundNumber?: boolean,
) => {
  if (quote.currentProvider) {
    return getCurrentPremiumAmount(quote, frequency);
  }

  if (quote.disabled) {
    return '';
  }

  const prem = getPremiumByFrequency(quote.premiums, frequency);

  if (prem) {
    const value = shouldRoundNumber ? Math.round(prem) : prem;
    return `$${prettifyNumber(value)}`;
  }

  if (toggledFrequency) {
    const { premium, frequency: quotePrem } = quote.premiums[0];
    const defaultPrem = convertFrequency(premium, quotePrem, toggledFrequency);

    if (defaultPrem) {
      const value = shouldRoundNumber ? Math.round(defaultPrem) : defaultPrem;
      return `$${prettifyNumber(value)}`;
    }
  }

  return 'N/A';
};

export const getDirectMarketScanFormId = (policyType: string = '') => {
  const name = policyType
    .replace('&', 'and')
    .replace(/\s/g, '-')
    .replace('/', '-')
    .toLowerCase();
  return `direct-market-scan-${name}`;
};

export const convertAccidentHistory = (props) => {
  const { policyType, accidentClaimCount, accidentHistory } = props;
  if (
    accidentHistory &&
    [INSURANCE_TYPES_NAMES.VEHICLE, INSURANCE_TYPES_NAMES.RV].includes(
      policyType,
    )
  ) {
    const accidentClaims = [];
    for (let index = 0; index < accidentClaimCount; index++) {
      const count = index + 1;
      accidentClaims.push({
        date: props[`accidentClaimDate${count}`],
        reason: props[`accidentFault${count}`]
          ? 'At fault accident'
          : 'Not at fault accident',
        details: props[`accidentDetails${count}`],
      });
    }
    return {
      accidentHistory,
      accidentClaims,
    };
  }

  return {};
};

export const getPolicyAmountLabel = (policyType: string) => {
  switch (policyType) {
    case INSURANCE_TYPES_NAMES.VEHICLE:
      return 'Sum insured';

    case INSURANCE_TYPES_NAMES.CONTENT:
      return 'Contents';

    case INSURANCE_TYPES_NAMES.HOME:
      return 'House';

    default:
      return 'Sum insured';
  }
};

export const isPremiumUnavailable = (quote: IQuote) =>
  quote?.premiums?.length === 0;

export const shouldShowRequestQuote = (
  quote: IQuote,
  policyType: string,
): boolean => {
  // Only for Vehicle insurance
  if (policyType !== INSURANCE_TYPES_NAMES.VEHICLE) {
    return false;
  }
  // Show if requestQuoteAvailable is true AND not yet requested
  return quote.requestQuoteAvailable === true && quote.quoteRequested !== true;
};

export const isQuoteRequestPending = (quote: IQuote): boolean => {
  // Requested but async processing not yet completed
  return quote.quoteRequested === true && !quote.quoteRequestCompletedAt;
};

export const getProviderById = (
  policyProviders: IPolicyProvider[],
  id: string,
) => policyProviders.find((p) => p.id === id);

export const isThirdPartyMarketScan = (policyScanRow: IPolicyScan) => {
  const { policySubTypeName } = policyScanRow;
  const policySubTypeNameLowercase = policySubTypeName?.toLowerCase();

  return (
    policySubTypeNameLowercase?.includes('third party') &&
    !policySubTypeNameLowercase?.includes('fire')
  );
};

export const hasAnyAutoMarketScan = (policyScans: IPolicyScan[]) => {
  return policyScans.some((policyScan) => policyScan.isAutoScan);
};

export const isVehiclePolicy = (policyType: string) =>
  policyType === INSURANCE_TYPES_NAMES.VEHICLE;


const CROSS_VALUES = [
  'no cover',
  'not covered',
  'not specified',
  'no offer',
  'no review',
  'not available',
];

const CHECK_VALUES = ['yes', 'included', 'free/included', 'covered'];

export const IsBenefitNoValue = ({
  value,
}: {
  value: string;
}) => {
  return (!value || R.includes(value.trim().toLowerCase(), CROSS_VALUES))
}

export const IsBenefitHasValue = ({
  value,
}: {
  value: string;
}) => {
  return (R.includes(value.trim().toLowerCase(), CHECK_VALUES))
}

export const hideCallout = (date: string) => {
  const CALLOUT_LIMIT = 7;
  return CALLOUT_LIMIT <= moment().diff(date, 'days');
};